/* ==========================================================================
   assets/upgrade-offer.js -- window.UpgradeOffer

   LOG-026, 28 Sep 2026 (00-COMPANY/09-decision-log.md) · caldwell-theme.
   The theme side of the offer machinery. BUILT SWITCHED OFF: with
   window.UpgradeFlags.offer absent (the merchant switch off, see
   layout/theme.liquid), every function here returns null / [] / false and
   nothing on the page can tell this file is loaded at all.

   WHAT THIS IS. Pure functions over two globals this file only READS:
     - window.UpgradeFlags.offer / .messages   (layout/theme.liquid, from
       Theme settings > Upgrade > Offers -- config/settings_schema.json)
     - window.UpgradePrices                    (snippets/upgrade-price-map
       .liquid): .get(sku), .getByCode(code, grade), .getWas(sku),
       .getWasByCode(code, grade), .skuFor(code, grade)
   No DOM reads, no DOM writes, no timers, no fetch, no sessionStorage. A
   future UI layer (the offer bar, PDP badge, homepage banner -- none of
   which are built yet; they wait on Omar's pick among the Fable designs)
   owns painting and owns any setInterval/requestAnimationFrame that redraws
   a countdown once a minute. This file only answers "what is true right
   now", given a `now` the caller supplies (or the real clock, if it does
   not).

   THE MECHANISM THIS ASSUMES (spec PP-02, ruled by LOG-026). An offer is the
   variant's OWN Shopify price, with the previous list price as compare-at,
   written by one writer at the offer's start and reverted at its end. So
   the "price" this file reports for a SKU is simply window.UpgradePrices'
   own figure -- every surface that already reads that map (the bonus, valU,
   Pay monthly, trade-in notes, the deal bar) keeps following it with no new
   maths, offer or not. This file adds only what the price map cannot state
   by itself: the offer's words, its window, and the lawful "was" figure
   turned into a saving and a percentage.

   FAIL CLOSED. A missing or malformed field anywhere in the offer record,
   an out-of-window `now`, a SKU with no live price, no lawful "was", a
   "was" at or under the price, or `available: false` (sold out) all answer
   null / false / [], never a guess. See validOffer() and validMessage().

   CAIRO TIME, WITHOUT A FIXED OFFSET. Every date field in Theme settings is
   entered as Cairo LOCAL wall-clock time, "YYYY-MM-DD HH:MM". Egypt moves to
   summer time (UTC+3) on the last Friday of April and back to standard time
   (UTC+2) on the last Thursday of October (2026: 30 Oct). Hard-coding either
   offset would put a Black Friday countdown up to an hour wrong depending on
   which side of that boundary "now" falls. cairoWallToUtcMs() instead asks
   Intl.DateTimeFormat, with timeZone 'Africa/Cairo', what the real calendar
   says -- the same IANA tz database Node and every modern browser ship --
   and converges on the UTC instant that reads as the requested Cairo wall
   time, iterating across the DST edge rather than assuming a single offset
   holds for the whole conversion.

   LIQUID'S PART IS DELIBERATELY SMALL. Shopify's own docs warn a Liquid
   `now`/`| date: '%s'` "might not be updated for every page view, depending
   on the context and caching" (shopify.dev/docs/api/liquid/filters/date), so
   a server-rendered "is the offer live" decision could leave a banner up
   after the offer has really ended, or hide one that has really started.
   layout/theme.liquid therefore gates ONLY on the merchant switch (does the
   theme editor say the offer is on) and publishes the raw settings fields
   as-is; EVERY date/window/validity decision happens here, in the browser,
   against the visitor's own clock. See layout/theme.liquid's own comment
   for the corollary: a wrong clock on a visitor's phone can only move what
   a label says, never what Shopify charges -- checkout always reads the
   live variant price, which this file never touches.

   Usage (future UI code; none of it exists yet):
     UpgradeOffer.active()                    -> Boolean, offer live right now
                                                  AND every display field
                                                  valid -- gates what a
                                                  customer can be SHOWN.
     UpgradeOffer.windowOpen()                -> Boolean, a real price cut is
                                                  in effect right now, words
                                                  aside (29 Sep 2026, review
                                                  fix) -- gates what a
                                                  customer is CHARGED (the
                                                  trade-up bonus's own rate).
     UpgradeOffer.forSku('IPHONE15-128-DS-RP') -> {price, was, saving, pct,
                                                    onOffer, name, ends,
                                                    termsUrl} | null. was/
                                                    saving/pct are null when
                                                    the previous price must
                                                    not be shown (29 Sep 2026,
                                                    see forSku()'s own
                                                    comment) -- the offer
                                                    itself is still returned.
     UpgradeOffer.forSku('IPHONE15-128-DS', 'p') -> same, by code + grade
     UpgradeOffer.forSku(sku, grade, { available: false }) -> null (sold out)
     UpgradeOffer.countdown()                 -> {days, hours, minutes} | null
     UpgradeOffer.messages()                  -> [{kind, text, link, ends}, ...]
   ========================================================================== */
(function () {
  'use strict';
  if (window.UpgradeOffer) { return; }

  var MESSAGE_SLOTS = 3;
  var KIND_PRICE = 'price';
  var MESSAGE_KINDS = { offer: true, partnership: true };

  /* ---- small helpers, no dependency on anything but the platform --------- */

  function isStr(v) { return typeof v === 'string'; }

  function nonBlank(v, maxLen) {
    if (!isStr(v)) return false;
    var t = v.trim();
    if (t.length === 0) return false;
    if (typeof maxLen === 'number' && t.length > maxLen) return false;
    return true;
  }

  function flags() {
    return (window.UpgradeFlags && typeof window.UpgradeFlags === 'object') ? window.UpgradeFlags : {};
  }

  function pricesApi() {
    var api = window.UpgradePrices;
    return (api && typeof api === 'object') ? api : null;
  }

  /* ---- Cairo local time -> UTC epoch ms, via the real calendar -----------
     "YYYY-MM-DD HH:MM", exactly. Anything else is invalid -- fail closed,
     not a best-effort parse of whatever the merchant typed. */
  var CAIRO_RE = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/;
  var CAIRO_FMT = (typeof Intl !== 'undefined' && Intl.DateTimeFormat)
    ? new Intl.DateTimeFormat('en-US', {
        timeZone: 'Africa/Cairo', hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      })
    : null;

  function cairoParts(date) {
    var out = {};
    CAIRO_FMT.formatToParts(date).forEach(function (p) {
      if (p.type !== 'literal') out[p.type] = parseInt(p.value, 10);
    });
    return out;
  }

  /* Days-in-month, leap years included, so "2026-02-30" is refused rather
     than silently rolling over into March the way `Date.UTC` would. */
  function daysInMonth(y, m) {
    return [31, ((y % 4 === 0 && y % 100 !== 0) || y % 400 === 0) ? 29 : 28,
      31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1];
  }

  /* Returns a UTC epoch ms, or null when the string is not a well-formed,
     calendar-valid "YYYY-MM-DD HH:MM". Never throws. */
  function cairoWallToUtcMs(str) {
    if (!isStr(str)) return null;
    var m = CAIRO_RE.exec(str.trim());
    if (!m) return null;
    var y = parseInt(m[1], 10), mo = parseInt(m[2], 10), d = parseInt(m[3], 10);
    var h = parseInt(m[4], 10), mi = parseInt(m[5], 10);
    if (mo < 1 || mo > 12) return null;
    if (d < 1 || d > daysInMonth(y, mo)) return null;
    if (h < 0 || h > 23) return null;
    if (mi < 0 || mi > 59) return null;
    if (!CAIRO_FMT) return null; // no Intl tz support: refuse rather than guess an offset

    var desired = Date.UTC(y, mo - 1, d, h, mi, 0, 0);
    var guess = desired;
    /* Converge on the UTC instant whose Cairo wall time reads exactly
       (y,mo,d,h,mi). Two passes is enough for a zone with one DST edge and
       no half-hour jump; a third pass costs nothing and guards a pass that
       lands exactly on the edge. */
    for (var i = 0; i < 3; i++) {
      var p = cairoParts(new Date(guess));
      var asIfUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, 0, 0);
      var diff = desired - asIfUtc;
      if (diff === 0) break;
      guess += diff;
    }
    /* Fail closed on a SKIPPED wall time (29 Sep 2026, review fix, minor
       finding on DST edges). Spring-forward jumps straight over one hour a
       year -- 2026: 00:00-00:59 on 24 Apr never happens, 23:59 EET is
       followed immediately by 01:00 EEST -- so no UTC instant reads back as
       that Cairo wall time. Without a check the loop above quietly settles
       an hour AND A DAY early (23:00 on 23 Apr for a request of 00:00 on 24
       Apr) instead of failing, which is exactly the silent guess this file's
       own header promises never to give ("FAIL CLOSED... never a guess").
       The round-trip below catches it: if the converged guess does not read
       back as the EXACT wall time requested, that time was never reached,
       and the caller gets null, same as any other malformed field.
       NOT covered by this check: the autumn REPEATED hour (23:00-23:59 on 29
       Oct 2026 happens twice, once at each offset). The loop still converges
       on a genuine, round-trip-correct instant for that wall time --
       consistently the LATER of the two occurrences -- so this is one of two
       right answers, not a wrong one, and round-tripping alone cannot tell
       the two apart. Distinguishing "genuinely ambiguous" from "genuinely
       resolved" needs its own neighbouring-hour check; not built here. Avoid
       setting an offer's start or end inside either transition hour. */
    var back = cairoParts(new Date(guess));
    if (back.year !== y || back.month !== mo || back.day !== d || back.hour !== h || back.minute !== mi) return null;
    return guess;
  }

  function toMs(now) {
    if (now instanceof Date) { var t = now.getTime(); return isFinite(t) ? t : Date.now(); }
    if (typeof now === 'number' && isFinite(now)) return now;
    return Date.now();
  }

  /* ---- the offer record: read once per call, never cached ---------------- */

  function rawOffer() {
    var o = flags().offer;
    return (o && typeof o === 'object') ? o : null;
  }

  /* A NAME/WHAT pair is a {en, ar} object with both sides present, so a
     merchant record with English filled in and Arabic left blank fails
     closed rather than shipping an English-only claim (Law 181/2018 Art 5;
     spec decision 9). */
  function pairOk(v, maxLen) {
    return !!v && typeof v === 'object' && nonBlank(v.en, maxLen) && nonBlank(v.ar, maxLen);
  }

  /* Every field C1 requires. See config/settings_schema.json's "Upgrade ·
     Offers" group for the merchant-facing side of each one. Length caps
     match the Fable design brief §3 (28/60 characters); they are a design
     constraint here, kept so a name that would not fit the bar cannot be
     "valid" in code while broken on screen. */
  function validOffer(o) {
    if (!o || typeof o !== 'object') return false;
    // 29 Sep 2026 (LOG-028): free delivery is standing policy, not an offer
    // kind, so KIND_PRICE is the only value this field can hold now.
    if (o.kind !== KIND_PRICE) return false;
    if (!pairOk(o.name, 28)) return false;
    if (!pairOk(o.what, 60)) return false;
    if (!nonBlank(o.termsUrl)) return false;
    var startMs = cairoWallToUtcMs(o.starts);
    var endMs = cairoWallToUtcMs(o.ends);
    if (startMs === null || endMs === null) return false;
    if (!(endMs > startMs)) return false;
    return true;
  }

  /* Live right now: switched on in the theme editor, every field valid, and
     `now` falls inside [starts, ends). Before the start or on/after the end,
     this is false and the offer "looks exactly as it does today" (spec
     C1) -- there is no separate "ended" state a customer can see. */
  function active(now) {
    var o = rawOffer();
    if (!o || o.enabled !== true) return false;
    if (!validOffer(o)) return false;
    var ms = toMs(now);
    var startMs = cairoWallToUtcMs(o.starts);
    var endMs = cairoWallToUtcMs(o.ends);
    return ms >= startMs && ms < endMs;
  }

  /* windowOpen(now) -- 29 Sep 2026, review fix (should-fix, LOG-029 gate).
     active()'s MONEY-ONLY twin. active() (above) fails closed on every
     DISPLAY field -- name, what, terms URL, the 28/60-char caps -- because a
     customer-facing badge or banner must never render a broken sentence.
     assets/upgrade-deal.js's offerRateFor() used to gate the trade-up
     bonus's OWN rate on active() too, which means a blank Arabic offer name
     (Arabic is paused site-wide today, LOG-016) silently switched the
     bonus back to the STANDING rate while window.UpgradePrices kept
     charging the REDUCED price -- the money moved, the words did not, and
     nothing said so. The bonus does not need a displayable name; it needs
     to know only whether a real price cut is in effect right now: the
     merchant switch, a price-kind offer, and a well-formed, open window.
     Never checks name/what/termsUrl. */
  function windowOpen(now) {
    var o = rawOffer();
    if (!o || o.enabled !== true) return false;
    if (o.kind !== KIND_PRICE) return false;
    var startMs = cairoWallToUtcMs(o.starts);
    var endMs = cairoWallToUtcMs(o.ends);
    if (startMs === null || endMs === null) return false;
    if (!(endMs > startMs)) return false;
    var ms = toMs(now);
    return ms >= startMs && ms < endMs;
  }

  /* ---- forSku(sku[, grade][, opts]) --------------------------------------
     opts.now       -- inject a clock for tests; defaults to Date.now().
     opts.available -- the future UI already knows whether the variant it is
                       painting is sold out (Liquid's own `variant.available`
                       or the same fact read some other way); UpgradePrices
                       carries no inventory field by design (see that
                       snippet's own header, "WHAT IS DELIBERATELY NOT IN THE
                       PAYLOAD"), so this file cannot discover "sold out" on
                       its own. REQUIRES `opts.available === true` -- fixed
                       29 Sep 2026, review fix (should-fix): an earlier
                       version defaulted a MISSING opts.available to "in
                       stock" so a caller who forgot to pass it would not
                       "silently lose every offer" -- but that is fail-OPEN,
                       the opposite of this whole file's own stated rule
                       ("FAIL CLOSED... an out-of-window `now`... all answer
                       null / false / [], never a guess", see the file
                       header) and of spec C3's sold-out guardrail (Art
                       9(11)/Exec Reg 8(13)): a caller that has not been
                       updated to pass availability must get null, not an
                       offer claim on stock that might not exist. There are
                       no real callers yet (the visible UI is not built), so
                       there is no compatibility cost to fixing this now. */
  function resolveSku(skuOrCode, grade) {
    if (!nonBlank(skuOrCode)) return null;
    if (grade === undefined || grade === null || grade === '') return skuOrCode;
    var api = pricesApi();
    if (!api || typeof api.skuFor !== 'function') return null;
    var full;
    try { full = api.skuFor(skuOrCode, grade); } catch (e) { return null; }
    return full || null;
  }

  /* 29 Sep 2026 (LOG-026 follow-up, spec defect: "an offer without a
     crossed-out price"). BEFORE this change forSku() returned null unless a
     LAWFUL "was" was on file and shown -- so a real price offer whose
     previous price does not yet meet the 30-day rule (spec PP-05) could not
     be displayed AT ALL. That is the wrong failure: the reduced price is
     still true and still charged; only the crossed-out COMPARISON is unsafe
     to show without the 30-day record.

     So "is this SKU actually reduced" and "is it LAWFUL to show what it was
     reduced FROM" are now two separate questions:
       - onOffer (isOnOffer(), price map) -- independent of "Show the
         previous price": true whenever compare_at is genuinely above price
         for this SKU (colours of one grade must agree, the same rule as
         price and was -- see upgrade-price-map.liquid).
       - was/saving/pct -- populated ONLY when offer.showWas === true AND
         the price map can state a lawful was for this exact SKU. Left null
         otherwise; forSku() still returns the offer (name, ends, terms,
         price, onOffer: true) so a badge with no struck price can be shown.
     A SKU the price map has no evidence for at all (isOnOffer false or
     unknown) is still not an offer -- forSku() returns null, same as today. */
  function forSku(skuOrCode, grade, opts) {
    var o = opts && typeof opts === 'object' ? opts : {};
    var now = toMs(o.now);
    if (!active(now)) return null;

    var offer = rawOffer();
    if (offer.kind !== KIND_PRICE) return null;

    // FAIL CLOSED (review fix, 29 Sep 2026): only an explicit `true` counts
    // as "in stock". See this function's own doc comment above.
    if (o.available !== true) return null;

    var sku = resolveSku(skuOrCode, grade);
    if (!sku) return null;

    var api = pricesApi();
    if (!api || typeof api.get !== 'function') return null;

    var price = api.get(sku);
    if (typeof price !== 'number' || !isFinite(price) || !(price > 0)) return null;

    // The price map's own fact, independent of offer.showWas -- see the
    // comment above this function.
    var onOffer = false;
    if (typeof api.isOnOffer === 'function') {
      try { onOffer = api.isOnOffer(sku) === true; } catch (e) { onOffer = false; }
    }
    if (!onOffer) return null;   // no evidence this SKU is reduced at all

    var was = null, saving = null, pct = null;
    if (offer.showWas === true && typeof api.getWas === 'function') {
      var w = null;
      try { w = api.getWas(sku); } catch (e) { w = null; }
      if (typeof w === 'number' && isFinite(w) && w > price) {
        was = w;
        saving = was - price;
        // Rounded DOWN, always (spec §3): 2,000/40,000 = 5.0% -> 5%;
        // 1,999/40,000 = 4.9975% -> 4%, never rounded up to a saving not
        // really given.
        //
        // INTEGER ARITHMETIC, 29 Sep 2026 (D11, review fix, LOG-027 Obsidian
        // foundation task F11). `Math.floor((saving / was) * 100)` computes
        // the division in floating point BEFORE multiplying by 100, and
        // that division is not always exact: 2,900 / 10,000 is the double
        // 0.28999999999999998..., which * 100 is 28.999999999999996, and
        // Math.floor of that is 28 -- one whole point low on a real,
        // clean 29% cut. The direction-b-obsidian prototype README's own
        // reviewer swept this and found 141 wrong percentages and 2,193
        // wrong prices across 10,000-100,000 (the 5-25% cuts this file's
        // existing tests happen to use are clean, which is why the bug
        // shipped unnoticed). Multiplying by 100 FIRST keeps the
        // intermediate value an integer whenever saving and was both are
        // (every SKU this file prices), so the division is exact:
        // 2,900 * 100 = 290,000; 290,000 / 10,000 = 29 exactly.
        // tests/offer.test.js SUITE 4 carries the 29/57/58% cases.
        pct = Math.floor((saving * 100) / was);
      }
    }

    return {
      price: price,
      was: was,
      saving: saving,
      pct: pct,
      onOffer: true,
      name: { en: offer.name.en, ar: offer.name.ar },
      ends: offer.ends,
      termsUrl: offer.termsUrl
    };
  }

  /* ---- countdown(now) -----------------------------------------------------
     Gated on active(), so it is null before the start (nothing shows yet),
     null after the end (the price has really reverted by then -- spec §0.1),
     and null when "Show a countdown" is off. Recomputed fresh from the fixed
     end date on every call: there is no stored state to restart, and the
     remaining time can only fall as `now` advances. Never negative -- the
     `active()` gate already guarantees ms < endMs before the subtraction
     runs. */
  function countdown(now) {
    var ms = toMs(now);
    if (!active(ms)) return null;
    var o = rawOffer();
    if (o.showCountdown !== true) return null;
    var endMs = cairoWallToUtcMs(o.ends);
    if (endMs === null) return null;
    var remainingMs = endMs - ms;
    if (!(remainingMs > 0)) return null;
    var minutes = Math.floor(remainingMs / (60 * 1000));
    var days = Math.floor(minutes / 1440);
    var hours = Math.floor((minutes - days * 1440) / 60);
    var mins = minutes - days * 1440 - hours * 60;
    return { days: days, hours: hours, minutes: mins };
  }

  /* ---- messages(now) -------------------------------------------------------
     Up to 3 "don't miss it" lines (Fable addendum §0.3), each independent of
     the offer above and of each other: a partner message can run with the
     price offer off, and vice versa. Returned in slot order (1, 2, 3); a
     slot that is off, out of window or missing a required field is left out
     entirely rather than returned as null, so callers can just iterate. */
  function validMessage(m) {
    if (!m || typeof m !== 'object') return false;
    if (!Object.prototype.hasOwnProperty.call(MESSAGE_KINDS, m.kind)) return false;
    if (!pairOk(m.text)) return false; // no length cap published for messages
    var startMs = cairoWallToUtcMs(m.starts);
    var endMs = cairoWallToUtcMs(m.ends);
    if (startMs === null || endMs === null) return false;
    if (!(endMs > startMs)) return false;
    return true;
  }

  function messages(now) {
    var ms = toMs(now);
    var list = flags().messages;
    if (!Array.isArray(list)) return [];
    var out = [];
    for (var i = 0; i < list.length && i < MESSAGE_SLOTS; i++) {
      var m = list[i];
      if (!m || m.enabled !== true) continue;
      if (!validMessage(m)) continue;
      var startMs = cairoWallToUtcMs(m.starts);
      var endMs = cairoWallToUtcMs(m.ends);
      if (!(ms >= startMs && ms < endMs)) continue;
      out.push({
        kind: m.kind,
        text: { en: m.text.en, ar: m.text.ar },
        link: nonBlank(m.link) ? m.link : null,
        ends: m.ends
      });
    }
    return out;
  }

  window.UpgradeOffer = {
    KIND_PRICE: KIND_PRICE,
    active: active,
    windowOpen: windowOpen,
    forSku: forSku,
    countdown: countdown,
    messages: messages,
    /* Exposed for tests and for a future UI that needs the same clock math
       (e.g. formatting "Until 31 Oct" in Cairo time). Not part of the
       null-safety contract above -- callers doing real work should use
       active()/forSku()/countdown()/messages(). */
    _cairoWallToUtcMs: cairoWallToUtcMs
  };
})();
